import { type Manifest } from '@doxyme/extensions-graphql-types';
import { Specs } from '@doxyme/extensions-common';
import { _getLogger } from '@doxyme/analytics';
import type { ProcessSideloadOptions } from './types';

const logger = _getLogger('parseSideloadParam', {
  component: 'extensions',
});

/**
 * Regular expression to match the sideload parameter format.
 * The format is: `baseUrl;encodedManifestJson`
 */
const SIDELOAD_PARAM_REGEX = /^([^;]+);(.+)$/;

export const SIDELOAD_PARAM_KEY = 'sideloadExtension';

/**
 * Extracts sideload parameters from the URL hash fragment.
 * The hash is never sent to the server, avoiding URL length limits.
 */
export const getSideloadParamsFromHash = (): string[] => {
  if (typeof window === 'undefined') return [];

  const hash = window.location.hash.slice(1); // Remove leading '#'
  if (!hash) return [];

  const params = new URLSearchParams(hash);
  return params.getAll(SIDELOAD_PARAM_KEY);
};

/**
 * Parses a sideload parameter value into base URL and manifest.
 *
 * @param value - The sideload parameter string
 * @returns Parsed sideload options or null if invalid
 */
export const parseSideloadParam = (
  value: string,
): ProcessSideloadOptions | null => {
  const match = SIDELOAD_PARAM_REGEX.exec(value);
  if (!match) {
    logger.error('[parseSideloadParam] Invalid sideload param format', {
      value,
    });
    return null;
  }

  // Both groups are guaranteed to exist when regex matches
  const baseUrl = match[1] as string;
  const encodedManifest = match[2] as string;

  try {
    new URL(baseUrl);
  } catch {
    logger.error('[parseSideloadParam] Invalid baseUrl', { baseUrl });
    return null;
  }

  try {
    const decodedManifest = decodeURIComponent(encodedManifest);
    const manifest = JSON.parse(decodedManifest) as Manifest;

    const validation = Specs.isValidManifest(manifest);
    if (!validation.isValid) {
      logger.error(
        '[parseSideloadParam] Invalid manifest - validation failed',
        { error: validation.error },
      );
      return null;
    }

    return { baseUrl, manifest };
  } catch (error) {
    logger.error('[parseSideloadParam] Failed to parse manifest JSON', {
      error,
    });
    return null;
  }
};
